import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import VerificationCodeInput from './components/VerificationCodeInput';
import VerificationStatus from './components/VerificationStatus';

const RESEND_COOLDOWN_SECONDS = 60;

const EmailVerification = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState('');
  const [status, setStatus] = useState('idle');
  const [statusMessage, setStatusMessage] = useState('');
  const [error, setError] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const isResendingRef = useRef(false);
  const [userEmail, setUserEmail] = useState('');
  const [resendCountdown, setResendCountdown] = useState(0);

  // ดึง email จาก location state → localStorage → supabase session (fallback)
  useEffect(() => {
    let cancelled = false;
    const loadEmail = async () => {
      let email = location?.state?.email || localStorage.getItem('pending_verification_email');

      // Fallback: ถ้า localStorage ถูกเคลียร์ แต่ user ยัง login อยู่ → ดึง email จาก session
      //          ป้องกัน loop /main-dashboard ↔ /email-verification ↔ /login-registration
      if (!email) {
        try {
          const { data: { user } } = await supabase.auth.getUser();
          email = user?.email || '';
        } catch { /* ignore */ }
      }

      if (cancelled) return;
      if (!email) {
        navigate('/login-registration');
        return;
      }

      // persist เพื่อให้ mount ครั้งถัดไปเจอเร็ว
      localStorage.setItem('pending_verification_email', email);

      setUserEmail(email);
      setStatusMessage(`We've sent an 8-character verification code to ${email}, please check your inbox.`);

      // คำนวณ resend cooldown จาก timestamp จริง
      const resendAt = localStorage.getItem('resend_cooldown_at');
      if (resendAt) {
        const elapsed = Math.floor((Date.now() - parseInt(resendAt, 10)) / 1000);
        const remaining = Math.max(RESEND_COOLDOWN_SECONDS - elapsed, 0);
        setResendCountdown(remaining);
      }
    };

    loadEmail();
    return () => { cancelled = true; };
  }, [location?.state, navigate]);

  // Countdown timer — Resend cooldown
  useEffect(() => {
    if (resendCountdown <= 0) return;

    const timer = setInterval(() => {
      const resendAt = localStorage.getItem('resend_cooldown_at');
      if (!resendAt) {
        setResendCountdown(0);
        clearInterval(timer);
        return;
      }
      const elapsed = Math.floor((Date.now() - parseInt(resendAt, 10)) / 1000);
      const remaining = Math.max(RESEND_COOLDOWN_SECONDS - elapsed, 0);
      setResendCountdown(remaining);
      if (remaining <= 0) {
        localStorage.removeItem('resend_cooldown_at');
        clearInterval(timer);
      }
    }, 1000);

    return () => clearInterval(timer);
  }, [resendCountdown]);

  // Auto-verify เมื่อกรอกครบ 8 ตัว (debounced กัน paste ผิดแล้วยิง verify ทันที)
  useEffect(() => {
    if (code?.length !== 8 || isVerifying || status === 'success') return;
    const t = setTimeout(() => handleVerification(), 250);
    return () => clearTimeout(t);
  }, [code]);

  const handleVerification = useCallback(async () => {
    if (!userEmail || code?.length !== 8) return;

    setIsVerifying(true);
    setError('');
    setStatus('verifying');
    setStatusMessage('Verifying your code...');

    try {
      // ใช้ custom RPC verify_email_code
      const { data, error: verifyError } = await supabase.rpc('verify_email_code', {
        p_email: userEmail,
        p_code: code,
      });

      if (verifyError) throw verifyError;

      const result = data;

      if (result?.status === 'success') {
        // ลบ localStorage
        localStorage.removeItem('pending_verification_email');
        localStorage.removeItem('resend_cooldown_at');

        // Set/refresh session marker — ป้องกัน AuthContext force-logout หลัง full reload
        // (เช่น return จาก Stripe Checkout)
        const isHttps = typeof window !== 'undefined' && window.location.protocol === 'https:';
        const secureFlag = isHttps ? '; Secure' : '';
        document.cookie = `session_active=1; path=/; SameSite=Lax${secureFlag}`;

        setStatus('success');
        setStatusMessage('Email verified successfully! Redirecting...');

        setTimeout(() => {
          navigate('/subscription-payment');
        }, 1500);
      } else if (result?.status === 'invalid') {
        setStatus('error');
        setStatusMessage('Invalid verification code.');
        setError('Invalid code. Please check and try again.');
        setIsVerifying(false);
      } else if (result?.status === 'not_found') {
        setStatus('error');
        setStatusMessage('No verification code found.');
        setError('Please request a new code.');
        setIsVerifying(false);
      } else {
        setStatus('error');
        setStatusMessage(result?.message || 'Verification failed.');
        setError(result?.message || 'Please try again.');
        setIsVerifying(false);
      }
    } catch (err) {
      if (import.meta.env?.DEV) console.error('Verify error:', err);
      setStatus('error');
      setStatusMessage('An error occurred. Please try again.');
      setError('Network error. Please try again.');
      setIsVerifying(false);
    }
  }, [userEmail, code, navigate]);

  const handleResendCode = async () => {
    if (!userEmail || isResendingRef.current) return;

    // ถ้าติด cooldown → แจ้งแต่ไม่ส่ง
    if (resendCountdown > 0) {
      setStatusMessage(`Please wait ${resendCountdown} seconds before requesting a new code.`);
      return;
    }

    // ล็อกทันที — ref (sync) + cooldown + state (UI)
    isResendingRef.current = true;
    setIsResending(true);
    localStorage.setItem('resend_cooldown_at', Date.now().toString());
    setResendCountdown(RESEND_COOLDOWN_SECONDS);
    setCode('');
    setError('');
    setStatus('idle');

    try {
      const { data: code, error: resendError } = await supabase.rpc('generate_email_code', {
        p_email: userEmail,
      });

      if (resendError) throw resendError;

      // ส่ง email ผ่าน Edge Function
      await supabase.functions.invoke('send-verification-email', {
        body: { email: userEmail, code },
      });

      setStatusMessage(`A new verification code has been sent to ${userEmail}.`);
    } catch (err) {
      if (import.meta.env?.DEV) console.error('Resend error:', err);
      // ถ้า error → ปลด cooldown ให้ลองใหม่ได้
      localStorage.removeItem('resend_cooldown_at');
      setResendCountdown(0);
      setStatusMessage('Failed to send new code. Please try again.');
    } finally {
      isResendingRef.current = false;
      setIsResending(false);
    }
  };

  const handleBackToRegister = async () => {
    // ต้องเคลียร์ session + pending email ก่อน ไม่งั้น /login-registration จะเด้งกลับมาที่ /email-verification
    localStorage.removeItem('pending_verification_email');
    localStorage.removeItem('resend_cooldown_at');
    try {
      await supabase.auth.signOut({ scope: 'local' });
    } catch { /* ignore */ }
    navigate('/login-registration', { replace: true });
  };

  return (
    <div className="min-h-screen bg-black flex items-center justify-center px-3 py-6 sm:p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-semibold text-foreground mb-2">
            Verify Your Email
          </h1>
          <p className="text-sm sm:text-base text-muted-foreground">
            Enter the verification code to activate your account
          </p>
        </div>

        <div className="relative rounded-2xl sm:rounded-3xl p-4 sm:p-8 overflow-hidden" style={{
          background: 'rgba(26, 26, 29, 0.4)',
          backdropFilter: 'blur(24px)',
          WebkitBackdropFilter: 'blur(24px)',
          border: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), inset 0 1px 0 rgba(255, 255, 255, 0.1)',
        }}>
          {/* Orange glow accents */}
          <div className="absolute top-0 left-0 w-32 h-32 rounded-full" style={{
            background: 'radial-gradient(circle, rgba(254, 131, 56, 0.15) 0%, rgba(249, 110, 34, 0.08) 40%, transparent 70%)',
            filter: 'blur(20px)',
            pointerEvents: 'none'
          }} />
          <div className="absolute top-0 right-0 w-32 h-32 rounded-full" style={{
            background: 'radial-gradient(circle, rgba(236, 103, 30, 0.15) 0%, rgba(244, 97, 24, 0.08) 40%, transparent 70%)',
            filter: 'blur(20px)',
            pointerEvents: 'none'
          }} />

          <div className="relative z-10">
            <VerificationStatus status={status} message={statusMessage} />


            <div className="mb-4 sm:mb-6">
              <label className="block text-xs sm:text-sm font-medium text-foreground mb-3 sm:mb-4 text-center">
                Enter 8-character verification code
              </label>
              <VerificationCodeInput
                code={code}
                onChange={setCode}
                error={error}
                disabled={isVerifying || status === 'success'}
              />
            </div>

            <div className="space-y-3 sm:space-y-4 mb-4 sm:mb-6">
              <button
                onClick={handleVerification}
                disabled={code?.length !== 8 || isVerifying || status === 'success'}
                className="w-full px-4 sm:px-6 py-3 sm:py-3.5 rounded-2xl bg-black text-white text-sm sm:text-base font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_30px_rgba(254,131,56,0.15),0_0_50px_rgba(249,110,34,0.1),0_0_70px_rgba(236,103,30,0.08)] shadow-[inset_0_2px_4px_rgba(255,255,255,0.1)] flex items-center justify-center gap-2"
              >
                {isVerifying ? (
                  <>
                    <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Verifying...</span>
                  </>
                ) : status === 'success' ? (
                  <span>Verified</span>
                ) : (
                  <span>Verify Email</span>
                )}
              </button>

              {/* Resend button */}
              <div className="text-center">
                <button
                  onClick={handleResendCode}
                  disabled={isVerifying || isResending || status === 'success' || resendCountdown > 0}
                  className="w-full px-4 sm:px-6 py-3 sm:py-3.5 rounded-2xl bg-black text-white text-sm sm:text-base font-medium transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed hover:shadow-[0_0_30px_rgba(254,131,56,0.15),0_0_50px_rgba(249,110,34,0.1),0_0_70px_rgba(236,103,30,0.08)] shadow-[inset_0_2px_4px_rgba(255,255,255,0.1)] flex items-center justify-center gap-2"
                >
                  <Icon name={isResending ? "Loader2" : resendCountdown > 0 ? "Clock" : "RefreshCw"} size={16} className={isResending ? 'animate-spin' : ''} />
                  {isResending ? 'Sending...' : resendCountdown > 0 ? `Resend in ${resendCountdown}s` : "Didn't receive the code?"}
                </button>
              </div>
            </div>

            <div className="mt-4 sm:mt-6">
              <Button
                variant="ghost"
                fullWidth
                onClick={handleBackToRegister}
                disabled={isVerifying}
                iconName="ArrowLeft"
                iconPosition="left"
                iconSize={16}
              >
                Back to register
              </Button>
            </div>
          </div>
        </div>

        <div className="mt-6 text-center">
          <p className="text-sm text-muted-foreground">
            Need help?{' '}
            <button className="text-primary hover:underline font-medium">
              Contact support
            </button>
          </p>
        </div>
      </div>
    </div>
  );
};

export default EmailVerification;
